/**
 * VideoRefsTab — paste IG reel URLs, save them tagged to a bucket + notes.
 * Preview via IG's public /embed/captioned iframe (no download, no auth).
 * Download button per ref pings the RapidAPI-backed edge fn (returns
 * 'not_configured' until a key is added).
 */
import { useState } from 'react';
import { Bookmark, Clapperboard, Download, ExternalLink, Eye, Film, Link as LinkIcon, Loader2, Plus, Sparkles, Trash2, Wand2 } from 'lucide-react';
import { SectionHeader } from '@/components/charts/SectionHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { TimedProgress } from '@/components/ui/TimedProgress';
import { EmptyState } from '@/components/charts/EmptyState';
import { cn } from '@/lib/utils';
import { useContentBuckets } from '@/hooks/useContentBuckets';
import {
  useVideoRefs, useAddVideoRef, useDeleteVideoRef, useDownloadVideoRef,
  useExtractStyle, usePreviewPrompt, useGenerateFromRef, useGeneratedVideos,
  usePlanShots, useGenerateReel, useSavedStyles, useSaveStyle, useDeleteSavedStyle,
  igEmbedUrl, type VideoRef, type GeneratedVideo, type ReelPlan,
} from '@/hooks/useVideoRefs';

export function VideoRefsTab() {
  const [bucketFilter, setBucketFilter] = useState<string | null>(null);
  const buckets = useContentBuckets();
  const refs = useVideoRefs(bucketFilter);

  return (
    <div className="space-y-4">
      <SectionHeader
        title={<span className="flex items-center gap-2"><Film className="h-4 w-4 text-violet-500" /> Video references</span>}
        description="Paste Instagram reel/post URLs. Preview inline. Tag with a bucket + notes so future prompts can lean on the vibe."
      />

      <AddForm buckets={buckets.data ?? []} />

      <SavedStylesStrip />

      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => setBucketFilter(null)}
          className={cn(
            'rounded-full border px-2.5 py-1 text-2xs transition-colors',
            !bucketFilter ? 'border-violet-500 bg-violet-500/10 text-foreground' : 'border-border/50 text-muted-foreground hover:text-foreground',
          )}
        >
          all
        </button>
        {(buckets.data ?? []).map((b) => (
          <button
            key={b.slug}
            type="button"
            onClick={() => setBucketFilter(b.slug)}
            className={cn(
              'flex items-center gap-1 rounded-full border px-2.5 py-1 text-2xs transition-colors',
              bucketFilter === b.slug ? 'border-violet-500 bg-violet-500/10 text-foreground' : 'border-border/50 text-muted-foreground hover:text-foreground',
            )}
          >
            <span className="h-2 w-2 rounded-full" style={{ background: b.color_accent ?? '#888' }} />
            {b.name}
          </button>
        ))}
      </div>

      {refs.isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-96 w-full" />)}
        </div>
      ) : (refs.data ?? []).length === 0 ? (
        <EmptyState title="No video references yet" description="Paste an IG reel/post URL above to save your first inspiration reference." />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {(refs.data ?? []).map((r) => <VideoRefCard key={r.id} ref_={r} buckets={buckets.data ?? []} />)}
        </div>
      )}
    </div>
  );
}

function AddForm({ buckets }: { buckets: Array<{ slug: string; name: string; color_accent: string | null }> }) {
  const [url, setUrl] = useState('');
  const [bucketSlug, setBucketSlug] = useState<string>('');
  const [notes, setNotes] = useState('');
  const add = useAddVideoRef();

  async function submit() {
    if (!url.trim()) return;
    try {
      await add.mutateAsync({ url: url.trim(), bucket_slug: bucketSlug || null, notes: notes.trim() || undefined });
      setUrl(''); setNotes('');
    } catch { /* toast handled below */ }
  }

  return (
    <Card>
      <CardContent className="space-y-2 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <LinkIcon className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="url"
              placeholder="https://www.instagram.com/reel/…"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
              className="w-full rounded-md border border-border/50 bg-background px-3 py-1.5 pl-7 text-sm placeholder:text-muted-foreground/60 focus:border-violet-500 focus:outline-none"
            />
          </div>
          <select
            value={bucketSlug}
            onChange={(e) => setBucketSlug(e.target.value)}
            className="rounded-md border border-border/50 bg-background px-2 py-1.5 text-sm"
          >
            <option value="">no bucket</option>
            {buckets.map((b) => <option key={b.slug} value={b.slug}>{b.name}</option>)}
          </select>
          <Button size="sm" onClick={submit} disabled={add.isPending || !url.trim()}>
            {add.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-1.5 h-3.5 w-3.5" />}
            Save
          </Button>
        </div>
        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes: what to lift from this ref (e.g. 'warm golden lighting, tight cheese-pull macro, quick 3-cut sequence')"
          className="w-full rounded-md border border-border/50 bg-background px-3 py-1.5 text-2xs"
        />
        {add.error && <div className="text-2xs text-rose-500">{(add.error as Error).message}</div>}
      </CardContent>
    </Card>
  );
}

function SavedStylesStrip() {
  const styles = useSavedStyles();
  const del = useDeleteSavedStyle();
  if (styles.isLoading) return null;
  const list = styles.data ?? [];
  if (list.length === 0) return null;
  return (
    <Card>
      <CardContent className="space-y-2 p-3">
        <div className="flex items-center gap-1.5 text-2xs font-medium">
          <Bookmark className="h-3 w-3 text-amber-500" />
          <span>Saved styles ({list.length})</span>
          <span className="text-muted-foreground/70">— extract once, apply to many dishes</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {list.map((s) => (
            <div
              key={s.id}
              className="group flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/5 px-2 py-1 text-2xs"
              title={s.prompt.slice(0, 400)}
            >
              {s.thumb_b2_key && (
                <img src={`/api/media/${s.thumb_b2_key}`} alt="" className="h-6 w-6 rounded object-cover" />
              )}
              <span className="max-w-[140px] truncate font-medium">{s.name}</span>
              {s.usage_count > 0 && (
                <span className="tabular-nums text-muted-foreground/70">·{s.usage_count}</span>
              )}
              <button
                type="button"
                onClick={() => { if (confirm(`Delete style "${s.name}"?`)) del.mutate(s.id); }}
                className="text-muted-foreground/40 opacity-0 transition-opacity hover:text-rose-500 group-hover:opacity-100"
                title="Delete preset"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function VideoRefCard({ ref_, buckets }: { ref_: VideoRef; buckets: Array<{ slug: string; name: string; color_accent: string | null }> }) {
  const del = useDeleteVideoRef();
  const dl = useDownloadVideoRef();
  const extract = useExtractStyle();
  const preview = usePreviewPrompt();
  const gen = useGenerateFromRef();
  const planShots = usePlanShots();
  const generateReel = useGenerateReel();
  const generated = useGeneratedVideos(ref_.id);
  const embed = igEmbedUrl(ref_);
  const [dlMsg, setDlMsg] = useState<string | null>(null);
  const [genMsg, setGenMsg] = useState<string | null>(null);
  const [subject, setSubject] = useState<string>('');
  const [promptOverride, setPromptOverride] = useState<string>('');
  const [showPrompt, setShowPrompt] = useState(false);
  const [durationS, setDurationS] = useState<number>(4);
  const [longForm, setLongForm] = useState<boolean>(false);
  const [fusedPreview, setFusedPreview] = useState<string | null>(null);
  const [reelTargetS, setReelTargetS] = useState<number>(24);
  const [reelPlan, setReelPlan] = useState<ReelPlan | null>(null);
  const [reelMsg, setReelMsg] = useState<string | null>(null);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const saveStyle = useSaveStyle();
  const savedStyles = useSavedStyles();
  // Effective style: either applied preset overrides the ref's extracted one
  const [appliedStyleId, setAppliedStyleId] = useState<string | null>(null);
  const appliedStyle = (savedStyles.data ?? []).find((s) => s.id === appliedStyleId) ?? null;
  const effectiveStylePrompt = appliedStyle?.prompt ?? ref_.extracted_style_prompt;
  const bucket = buckets.find((b) => b.slug === ref_.bucket_slug);
  const hasBytes = !!ref_.b2_video_key;
  const hasStyle = !!effectiveStylePrompt;

  async function download() {
    setDlMsg(null);
    try {
      const r = await dl.mutateAsync(ref_.id);
      setDlMsg(r.ok ? '✅ downloaded' : (r.error ?? 'failed'));
    } catch (e) { setDlMsg((e as Error).message); }
  }

  async function extractStyle() {
    setGenMsg(null);
    try {
      const r = await extract.mutateAsync(ref_.id);
      setGenMsg(`✨ style extracted (${(r.extracted_ms / 1000).toFixed(1)}s)`);
      setShowPrompt(true);
    } catch (e) { setGenMsg((e as Error).message); }
  }

  async function previewPrompt() {
    if (!subject.trim() && !promptOverride.trim()) {
      setGenMsg('add a dish + ingredients first');
      return;
    }
    setGenMsg('🧪 fusing prompt via Claude…');
    try {
      const r = await preview.mutateAsync({
        ref_id: ref_.id,
        subject: subject.trim() || undefined,
        prompt_override: promptOverride.trim() || undefined,
        style_override: appliedStyle?.prompt,
        long_form: longForm,
        duration_s: durationS,
      });
      setFusedPreview(r.prompt);
      setShowPrompt(true);
      setGenMsg(`✨ prompt ready (${r.prompt_source}) — review below, then hit Generate`);
    } catch (e) { setGenMsg('❌ ' + (e as Error).message); }
  }

  async function planReel() {
    if (!subject.trim()) { setReelMsg('add a dish + ingredients first (needed for shot planning)'); return; }
    setReelMsg('📝 Claude storyboarding shots…');
    try {
      const plan = await planShots.mutateAsync({ ref_id: ref_.id, subject: subject.trim(), target_seconds: reelTargetS, style_override: appliedStyle?.prompt });
      setReelPlan(plan);
      setReelMsg(`✨ ${plan.shot_count} shots × ${plan.per_shot_seconds}s planned — review + edit prompts, then Generate all`);
    } catch (e) { setReelMsg('❌ ' + (e as Error).message); }
  }

  function editShot(idx: number, key: 'prompt' | 'beat', value: string) {
    if (!reelPlan) return;
    setReelPlan({
      ...reelPlan,
      shots: reelPlan.shots.map((s, i) => (i === idx ? { ...s, [key]: value } : s)),
    });
  }

  async function generateAllShots() {
    if (!reelPlan) return;
    const reelId = crypto.randomUUID();
    setReelMsg(`🎬 firing ${reelPlan.shot_count} shots in parallel — each ~40s, watch tiles below…`);
    try {
      await generateReel.mutateAsync({
        ref_id: ref_.id,
        reel_id: reelId,
        shots: reelPlan.shots.map((s) => ({ shot_index: s.shot_index, duration_s: s.duration_s, prompt: s.prompt, beat: s.beat })),
        long_form: longForm,
        style_override: appliedStyle?.prompt,
      });
      setReelMsg(`✅ ${reelPlan.shot_count} shots kicked off — reel id ${reelId.slice(0, 8)}. Shots appear below as they finish.`);
      setReelPlan(null);
    } catch (e) { setReelMsg('❌ ' + (e as Error).message); }
  }

  async function generateVideo() {
    if (!subject.trim() && !promptOverride.trim() && !fusedPreview) {
      setGenMsg('add a dish + ingredients first (or edit the prompt directly)');
      return;
    }
    setGenMsg('🎬 Veo generating… ~40s');
    try {
      const r = await gen.mutateAsync({
        ref_id: ref_.id,
        subject: fusedPreview ? undefined : (subject.trim() || undefined),
        prompt_override: fusedPreview ?? (promptOverride.trim() || undefined),
        style_override: appliedStyle?.prompt,
        aspect_ratio: '9:16',
        duration_s: durationS,
        long_form: longForm,
      });
      setGenMsg(`✅ ${durationS}s clip in ${(r.generated_ms / 1000).toFixed(0)}s · ${(r.bytes / 1_000_000).toFixed(1)}MB · ${longForm ? 'Veo 3.1 pro' : 'Veo 3.1 fast'}`);
      setFusedPreview(null);
    } catch (e) { setGenMsg('❌ ' + (e as Error).message); }
  }

  return (
    <Card className="overflow-hidden">
      <div className="relative aspect-[9/16] bg-black">
        {ref_.b2_video_key ? (
          <video
            src={`/api/media/${ref_.b2_video_key}`}
            poster={ref_.b2_thumb_key ? `/api/media/${ref_.b2_thumb_key}` : undefined}
            controls
            playsInline
            preload="metadata"
            className="h-full w-full"
          />
        ) : embed ? (
          <iframe
            src={embed}
            className="h-full w-full border-0"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            loading="lazy"
            title={ref_.source_url}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-2xs text-muted-foreground">
            invalid URL
          </div>
        )}
      </div>
      <CardContent className="space-y-2 p-3">
        <div className="flex items-center gap-2 text-2xs">
          {bucket ? (
            <span className="flex items-center gap-1 rounded-full bg-muted/40 px-1.5 py-0.5 text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: bucket.color_accent ?? '#888' }} />
              {bucket.name}
            </span>
          ) : (
            <span className="rounded-full bg-muted/40 px-1.5 py-0.5 text-muted-foreground">unbucketed</span>
          )}
          {hasBytes && <span className="rounded bg-emerald-500/10 px-1.5 py-0.5 text-2xs text-emerald-500">downloaded</span>}
          <span className="ml-auto tabular-nums text-muted-foreground/70">
            {new Date(ref_.added_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
          </span>
        </div>

        {ref_.notes && (
          <p className="text-2xs italic text-muted-foreground line-clamp-3">"{ref_.notes}"</p>
        )}

        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          <a
            href={ref_.source_url}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-2xs text-violet-500 hover:underline"
          >
            <ExternalLink className="h-3 w-3" /> IG
          </a>
          <Button size="sm" variant="outline" onClick={download} disabled={dl.isPending}>
            {dl.isPending ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : <Download className="mr-1.5 h-3 w-3" />}
            {hasBytes ? 'Re-DL' : 'Download'}
          </Button>
          {hasBytes && (
            <Button size="sm" variant="outline" onClick={extractStyle} disabled={extract.isPending}>
              {extract.isPending ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : <Sparkles className="mr-1.5 h-3 w-3" />}
              {ref_.extracted_style_prompt ? 'Re-extract' : 'Extract style'}
            </Button>
          )}
          {(savedStyles.data ?? []).length > 0 && (
            <select
              value={appliedStyleId ?? ''}
              onChange={(e) => setAppliedStyleId(e.target.value || null)}
              className="rounded-md border border-amber-500/40 bg-amber-500/5 px-1.5 py-0.5 text-2xs"
              title="Apply a saved style preset instead of this ref's own extraction"
            >
              <option value="">use this ref's style</option>
              {(savedStyles.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>★ {s.name}</option>
              ))}
            </select>
          )}
          {hasStyle && (
            <>
              <div className="flex items-center gap-0.5 rounded-md border border-border/50 p-0.5" title="Veo Generative Language API caps every clip at 8s — longer reels need multi-clip stitching (later)">
                {[4, 6, 8].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setDurationS(s)}
                    className={cn(
                      'rounded px-1.5 py-0.5 text-2xs font-medium tabular-nums transition-colors',
                      durationS === s ? 'bg-violet-500 text-white' : 'text-muted-foreground hover:text-foreground',
                    )}
                    title={`${s}s clip · ~₹${s * (longForm ? 100 : 20)}`}
                  >
                    {s}s
                  </button>
                ))}
              </div>
              <label className="flex cursor-pointer items-center gap-1 text-2xs" title="Veo 3.1 pro — higher quality, ~5× cost, still 8s max per clip">
                <input
                  type="checkbox"
                  checked={longForm}
                  onChange={(e) => setLongForm(e.target.checked)}
                  className="h-3 w-3 cursor-pointer accent-violet-500"
                />
                <span className="text-muted-foreground">pro quality</span>
              </label>
              <Button size="sm" variant="outline" onClick={previewPrompt} disabled={preview.isPending || gen.isPending} title="Fuse ref style + your dish/ingredients into the final Veo prompt (no video, no cost)">
                {preview.isPending ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : <Eye className="mr-1.5 h-3 w-3" />}
                Preview
              </Button>
              <Button size="sm" onClick={generateVideo} disabled={gen.isPending || preview.isPending}>
                {gen.isPending ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : <Wand2 className="mr-1.5 h-3 w-3" />}
                Generate {durationS}s
              </Button>
            </>
          )}
          <Button size="sm" variant="ghost" onClick={() => del.mutate(ref_.id)} disabled={del.isPending}>
            <Trash2 className="h-3 w-3" />
          </Button>
          {(dlMsg || genMsg) && (
            <span className={cn('text-2xs', (dlMsg ?? genMsg ?? '').startsWith('✅') || (dlMsg ?? genMsg ?? '').startsWith('✨') ? 'text-emerald-500' : 'text-amber-500')}>
              {dlMsg ?? genMsg}
            </span>
          )}
        </div>

        {(dl.isPending || extract.isPending || preview.isPending || gen.isPending) && (
          <div className="space-y-1 pt-1">
            {dl.isPending && (
              <TimedProgress expectedMs={15_000} label="⬇ downloading from Instagram + uploading to B2" tone="amber" />
            )}
            {extract.isPending && (
              <TimedProgress expectedMs={18_000} label="🔎 Gemini watching the reel to extract style" tone="violet" />
            )}
            {preview.isPending && (
              <TimedProgress expectedMs={6_000} label="🧪 Claude fusing style + dish into Veo prompt" tone="violet" />
            )}
            {gen.isPending && (
              <TimedProgress expectedMs={longForm ? 90_000 : 40_000} label={`🎬 Veo ${longForm ? '3.1 pro' : '3.1 fast'} generating ${durationS}s clip`} tone="emerald" />
            )}
          </div>
        )}

        {hasStyle && (
          <div className="mt-2 space-y-2 rounded-md border border-border/40 bg-muted/10 p-2">
            <div>
              <label className="mb-1 block text-2xs font-medium text-muted-foreground">
                Dish + ingredients (required — this becomes the SUBJECT Veo shows)
              </label>
              <textarea
                rows={2}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g. Shawarma Chicken Bowl — grilled chicken, tahini drizzle, pickled onions, sumac, cucumber, mint"
                className="w-full rounded-md border border-border/50 bg-background px-2 py-1 text-2xs placeholder:text-muted-foreground/60"
              />
              <div className="mt-1 text-2xs text-muted-foreground/70">
                Claude fuses your subject with the extracted style before Veo runs.
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setShowPrompt((x) => !x)}
                className="flex flex-1 items-center justify-between text-2xs font-medium text-muted-foreground hover:text-foreground"
              >
                <span className="flex items-center gap-1">
                  <Sparkles className="h-3 w-3 text-violet-500" />
                  {fusedPreview
                    ? 'Final Veo prompt (fused — edit before generating)'
                    : appliedStyle
                      ? `Style preset: ${appliedStyle.name}`
                      : 'Extracted style (from this ref)'}
                </span>
                <span>{showPrompt ? '▾' : '▸'}</span>
              </button>
              {ref_.extracted_style_prompt && !fusedPreview && !appliedStyle && (
                <button
                  type="button"
                  onClick={() => {
                    const defaultName = ref_.notes?.trim() || ref_.author_handle || 'style';
                    const name = prompt('Name this style preset:', defaultName);
                    if (!name?.trim()) return;
                    setSaveMsg('💾 saving…');
                    saveStyle.mutateAsync({
                      name: name.trim(),
                      source_ref_id: ref_.id,
                      prompt: ref_.extracted_style_prompt!,
                      bucket_slug: ref_.bucket_slug,
                      thumb_b2_key: ref_.b2_thumb_key ?? undefined,
                      notes: ref_.notes ?? undefined,
                    })
                      .then(() => setSaveMsg('✅ saved — reuse from picker above'))
                      .catch((e: Error) => setSaveMsg('❌ ' + e.message));
                  }}
                  disabled={saveStyle.isPending}
                  className="flex items-center gap-1 rounded border border-amber-500/40 bg-amber-500/5 px-1.5 py-0.5 text-2xs text-amber-600 hover:bg-amber-500/10"
                  title="Save this extracted style as a reusable preset"
                >
                  {saveStyle.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Bookmark className="h-3 w-3" />}
                  Save style
                </button>
              )}
            </div>
            {saveMsg && (
              <div className={cn('text-2xs', saveMsg.startsWith('✅') ? 'text-emerald-500' : saveMsg.startsWith('❌') ? 'text-rose-500' : 'text-amber-500')}>
                {saveMsg}
              </div>
            )}
            {showPrompt && (
              <>
                <textarea
                  rows={fusedPreview ? 10 : 4}
                  value={fusedPreview ?? (promptOverride || effectiveStylePrompt || '')}
                  onChange={(e) => {
                    if (fusedPreview !== null) setFusedPreview(e.target.value);
                    else setPromptOverride(e.target.value);
                  }}
                  className="w-full rounded-md border border-border/50 bg-background px-2 py-1 text-2xs"
                  placeholder="Style prompt (auto-fused with subject; edit here to override)"
                />
                <div className="mt-1 flex items-center justify-between text-2xs text-muted-foreground/70">
                  <span>
                    {fusedPreview
                      ? 'This is exactly what Veo will receive. Tweak wording, ingredient callouts, camera moves — Generate uses this verbatim.'
                      : 'Edit this + leave subject empty → skips fusion, feeds this to Veo directly.'}
                  </span>
                  {fusedPreview && (
                    <button type="button" onClick={() => setFusedPreview(null)} className="ml-2 shrink-0 text-violet-500 hover:underline">
                      reset
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {hasStyle && (
          <div className="mt-2 space-y-2 rounded-md border border-violet-500/30 bg-violet-500/5 p-2">
            <div className="flex items-center gap-1.5 text-2xs font-medium text-violet-500">
              <Clapperboard className="h-3 w-3" /> Reel builder — longer than 8s
            </div>
            <div className="text-2xs text-muted-foreground/70">
              Veo caps each clip at 8s. This plans N coordinated shots with continuity (same countertop, lighting, palette, hand) so playback looks seamless without stitching. Drop the clips into your editor back-to-back — no edits needed.
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <div className="flex items-center gap-0.5 rounded-md border border-border/50 p-0.5">
                {[16, 24, 30, 45, 60].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setReelTargetS(s)}
                    className={cn(
                      'rounded px-1.5 py-0.5 text-2xs font-medium tabular-nums transition-colors',
                      reelTargetS === s ? 'bg-violet-500 text-white' : 'text-muted-foreground hover:text-foreground',
                    )}
                    title={`≈ ${Math.ceil(s / 8)} shots × 8s`}
                  >
                    {s}s
                  </button>
                ))}
              </div>
              <span className="text-2xs text-muted-foreground/70 tabular-nums">
                = {Math.ceil(reelTargetS / 8)} shots
              </span>
              <Button size="sm" variant="outline" onClick={planReel} disabled={planShots.isPending}>
                {planShots.isPending ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : <Sparkles className="mr-1.5 h-3 w-3" />}
                Plan shots
              </Button>
              {reelPlan && (
                <Button size="sm" onClick={generateAllShots} disabled={generateReel.isPending}>
                  {generateReel.isPending ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : <Wand2 className="mr-1.5 h-3 w-3" />}
                  Generate all {reelPlan.shot_count}
                </Button>
              )}
            </div>
            {reelMsg && (
              <div className={cn('text-2xs', reelMsg.startsWith('✅') || reelMsg.startsWith('✨') ? 'text-emerald-500' : reelMsg.startsWith('❌') ? 'text-rose-500' : 'text-amber-500')}>
                {reelMsg}
              </div>
            )}
            {planShots.isPending && (
              <TimedProgress expectedMs={12_000} label="📝 Claude storyboarding shots with continuity anchors" tone="violet" />
            )}
            {reelPlan && (
              <div className="space-y-2">
                {reelPlan.narrative && (
                  <div className="rounded bg-muted/30 p-1.5 text-2xs italic text-muted-foreground">
                    {reelPlan.narrative}
                  </div>
                )}
                {reelPlan.shots.map((s, i) => (
                  <div key={i} className="rounded border border-border/40 bg-background p-1.5">
                    <div className="mb-1 flex items-center gap-1.5 text-2xs font-medium">
                      <span className="rounded bg-violet-500/20 px-1.5 py-0.5 text-violet-500 tabular-nums">
                        {s.shot_index + 1}/{reelPlan.shots.length}
                      </span>
                      <input
                        value={s.beat}
                        onChange={(e) => editShot(i, 'beat', e.target.value)}
                        className="flex-1 border-0 bg-transparent p-0 text-2xs font-medium focus:outline-none"
                      />
                      <span className="tabular-nums text-muted-foreground/70">{s.duration_s}s</span>
                    </div>
                    <textarea
                      rows={4}
                      value={s.prompt}
                      onChange={(e) => editShot(i, 'prompt', e.target.value)}
                      className="w-full rounded border border-border/40 bg-muted/20 px-1.5 py-1 text-2xs"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {generated.data && generated.data.length > 0 && (
          <GeneratedList videos={generated.data} />
        )}
      </CardContent>
    </Card>
  );
}

function GeneratedList({ videos }: { videos: GeneratedVideo[] }) {
  // Group by reel_id (null = standalone singles)
  const reels = new Map<string, GeneratedVideo[]>();
  const singles: GeneratedVideo[] = [];
  for (const v of videos) {
    if (v.reel_id) {
      const arr = reels.get(v.reel_id) ?? [];
      arr.push(v);
      reels.set(v.reel_id, arr);
    } else {
      singles.push(v);
    }
  }
  const reelEntries = Array.from(reels.entries()).sort(([, a], [, b]) => {
    const at = a[0]?.created_at ?? '';
    const bt = b[0]?.created_at ?? '';
    return new Date(bt).getTime() - new Date(at).getTime();
  });

  return (
    <div className="mt-2 space-y-3">
      <div className="text-2xs font-medium text-muted-foreground">Generated ({videos.length})</div>
      {reelEntries.map(([reelId, shots]) => {
        const sorted = [...shots].sort((a, b) => (a.shot_index ?? 0) - (b.shot_index ?? 0));
        const total = sorted[0]?.shot_total ?? sorted.length;
        const done = sorted.filter((s) => s.status === 'done').length;
        const failed = sorted.filter((s) => s.status === 'failed').length;
        return (
          <div key={reelId} className="rounded-md border border-violet-500/30 bg-violet-500/5 p-2">
            <div className="mb-1.5 flex items-center gap-1.5 text-2xs font-medium">
              <Clapperboard className="h-3 w-3 text-violet-500" />
              <span className="text-violet-500">Reel {reelId.slice(0, 8)}</span>
              <span className="text-muted-foreground/70 tabular-nums">{done}/{total} done{failed > 0 ? ` · ${failed} failed` : ''}</span>
              <span className="ml-auto text-muted-foreground/70">Drop shots into your editor in order — no cuts needed</span>
            </div>
            <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${Math.min(total, 4)}, minmax(0, 1fr))` }}>
              {sorted.map((g) => <ShotTile key={g.id} gen={g} total={total} />)}
            </div>
          </div>
        );
      })}
      {singles.map((g) => <GeneratedVideoRow key={g.id} gen={g} />)}
    </div>
  );
}

function ShotTile({ gen, total }: { gen: GeneratedVideo; total: number }) {
  const label = `${(gen.shot_index ?? 0) + 1}/${total}`;
  if (gen.status !== 'done' || !gen.b2_video_key) {
    const isPro = gen.model?.includes('generate-preview') && !gen.model.includes('fast');
    const startedAt = new Date(gen.created_at).getTime();
    return (
      <div className="aspect-[9/16] rounded border border-border/40 bg-muted/20 p-1.5 text-2xs">
        <div className="flex items-center gap-1">
          <span className="rounded bg-violet-500/20 px-1 py-0.5 text-violet-500 tabular-nums">{label}</span>
          {gen.status === 'running' && <Loader2 className="h-3 w-3 animate-spin text-violet-500" />}
          {gen.status === 'failed' && <span className="text-rose-500">failed</span>}
        </div>
        {gen.shot_description && <div className="mt-1 text-muted-foreground line-clamp-2">{gen.shot_description}</div>}
        {gen.status === 'running' && (
          <TimedProgress className="mt-1.5" expectedMs={isPro ? 90_000 : 40_000} startedAt={startedAt} tone="emerald" />
        )}
        {gen.error && <div className="mt-1 text-rose-500 line-clamp-2">{gen.error.slice(0, 120)}</div>}
      </div>
    );
  }
  const url = `/api/media/${gen.b2_video_key}`;
  return (
    <div className="rounded border border-border/40 bg-background p-1">
      <div className="mb-1 flex items-center gap-1 text-2xs">
        <span className="rounded bg-violet-500/20 px-1 py-0.5 text-violet-500 tabular-nums">{label}</span>
        <a href={url} download={`flax-reel-${gen.reel_id?.slice(0, 8)}-shot-${(gen.shot_index ?? 0) + 1}-of-${total}.mp4`} className="ml-auto text-violet-500 hover:underline">
          <Download className="h-3 w-3" />
        </a>
      </div>
      <video src={url} controls playsInline preload="metadata" className="w-full rounded" />
      {gen.shot_description && <div className="mt-1 line-clamp-2 text-2xs text-muted-foreground">{gen.shot_description}</div>}
    </div>
  );
}

function GeneratedVideoRow({ gen }: { gen: GeneratedVideo }) {
  if (gen.status !== 'done' || !gen.b2_video_key) {
    const isPro = gen.model?.includes('generate-preview') && !gen.model.includes('fast');
    const startedAt = new Date(gen.created_at).getTime();
    return (
      <div className="rounded-md border border-border/30 bg-muted/10 p-2 text-2xs">
        <div className="flex items-center gap-1.5">
          {gen.status === 'running' && <Loader2 className="h-3 w-3 animate-spin text-violet-500" />}
          <span className={cn('rounded px-1.5 py-0.5 uppercase',
            gen.status === 'running' ? 'bg-violet-500/10 text-violet-500'
              : gen.status === 'failed' ? 'bg-rose-500/10 text-rose-500'
              : 'bg-muted/50 text-muted-foreground')}>
            {gen.status}
          </span>
          <span className="text-muted-foreground/70">{new Date(gen.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
        </div>
        {gen.status === 'running' && (
          <TimedProgress className="mt-1.5" expectedMs={isPro ? 90_000 : 40_000} label={`Veo ${isPro ? '3.1 pro' : '3.1 fast'} generating`} startedAt={startedAt} tone="emerald" />
        )}
        {gen.error && <div className="mt-1 text-rose-500">{gen.error.slice(0, 200)}</div>}
      </div>
    );
  }
  const videoUrl = `/api/media/${gen.b2_video_key}`;
  return (
    <div className="rounded-md border border-border/40 bg-muted/10 p-2">
      <video src={videoUrl} controls playsInline preload="metadata" className="w-full rounded" />
      <div className="mt-1 flex items-center gap-2 text-2xs text-muted-foreground">
        <span className="rounded bg-emerald-500/10 px-1.5 py-0.5 text-emerald-500">done</span>
        {gen.duration_s && <span>{gen.duration_s}s</span>}
        {gen.aspect_ratio && <span>{gen.aspect_ratio}</span>}
        {gen.generated_ms && <span>{(gen.generated_ms / 1000).toFixed(0)}s gen</span>}
        <a
          href={videoUrl}
          download={`flax-generated-${gen.id.slice(0, 8)}.mp4`}
          className="ml-auto flex items-center gap-1 text-violet-500 hover:underline"
        >
          <Download className="h-3 w-3" /> download
        </a>
      </div>
    </div>
  );
}
